import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { io, Socket } from 'socket.io-client'
import { API_CONFIG } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import type { SystemIssue, SystemIssueMessage } from '../types/system-issues'
import { issueKey, myIssuesKey } from './useSystemIssues'
import { issueMessagesKey } from './useSystemIssueMessages'

interface UseSystemIssuesSocketOptions {
  issueId: number | null
}

/**
 * Namespace `/system-issues`, separado de `/occurrences` — mesmo esqueleto
 * de `useOccurrencesSocket.ts`, incluindo o cuidado com reconexão: o handler
 * de `connect` reemite `join_issue` a cada (re)conexão, não só na primeira
 * montagem — sem isso, um reconnect deixaria o socket fora da room da issue
 * e a thread pararia de receber updates em tempo real silenciosamente (bug
 * já caçado e corrigido em Ocorrências nesta mesma base).
 */
export function useSystemIssuesSocket({ issueId }: UseSystemIssuesSocketOptions) {
  const qc = useQueryClient()
  const { user, token } = useAuth()
  const socketRef = useRef<Socket | null>(null)
  const currentIssueRef = useRef<number | null>(null)

  useEffect(() => {
    if (!token || !user) return
    const socket = io(`${API_CONFIG.BASE_URL}/system-issues`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: { token },
    })
    socketRef.current = socket
    socket.on('connect', () => {
      socket.emit('system-issues.join_user')
      if (currentIssueRef.current) {
        socket.emit('system-issues.join_issue', {
          issueId: currentIssueRef.current,
        })
      }
    })

    // Payload escalar (entidade crua, atores não populados) — handler só
    // invalida cache por id, refetch traz o shape completo (UserRef).
    socket.on(
      'issue.created',
      (issue: Pick<SystemIssue, 'id' | 'created_by_id'>) => {
        void issue
        qc.invalidateQueries(myIssuesKey)
        qc.invalidateQueries(['system-issues', 'all'])
      },
    )

    socket.on('issue.updated', (issue: Pick<SystemIssue, 'id'>) => {
      qc.invalidateQueries(issueKey(issue.id))
      qc.invalidateQueries(['system-issues', 'all'])
      qc.invalidateQueries(myIssuesKey)
    })

    socket.on(
      'message.created',
      (message: Pick<SystemIssueMessage, 'id' | 'issue_id'>) => {
        qc.invalidateQueries(issueMessagesKey(message.issue_id))
        qc.invalidateQueries(issueKey(message.issue_id))
        qc.invalidateQueries(['system-issues', 'all'])
        qc.invalidateQueries(myIssuesKey)
      },
    )

    // Emitido só pra `user:{account_id}` — mantém outras abas/dispositivos
    // do MESMO usuário em sincronia quando ele marca uma issue como lida em
    // uma delas (o badge de não-lidas some em todas, não só na que agiu).
    socket.on('read.updated', () => {
      qc.invalidateQueries(myIssuesKey)
      qc.invalidateQueries(['system-issues', 'all'])
    })

    // Emitido só pra `user:{mentioned_account_id}` quando alguém marca esse
    // usuário numa issue — "Meus problemas" passa a incluí-la sem precisar
    // recarregar a página.
    socket.on('mention.added', (data: { issue_id: number }) => {
      qc.invalidateQueries(myIssuesKey)
      qc.invalidateQueries(issueKey(data.issue_id))
    })

    return () => {
      socket.close()
      socketRef.current = null
    }
  }, [token, user, qc])

  useEffect(() => {
    const socket = socketRef.current
    if (!socket) return
    const prev = currentIssueRef.current
    if (prev && prev !== issueId) {
      socket.emit('system-issues.leave_issue', { issueId: prev })
    }
    if (issueId) {
      socket.emit('system-issues.join_issue', { issueId })
    }
    currentIssueRef.current = issueId
  }, [issueId])
}
